/**
 * @fileoverview Histórico de execuções do fluxo: cada contato que entrou no
 * período, com estado, etapa atual, tempo, mensagens e o último erro. A lista
 * carrega por cursor ("Carregar mais"): a página seguinte começa depois da
 * última linha vista, sem pular nem repetir quando chegam execuções novas.
 */

import React, { useCallback, useEffect, useRef, useState } from "react";
import { MenuItem, useMediaQuery } from "@material-ui/core";
import { History, Search } from "lucide-react";
import ModernSelect from "../../../components/ModernSelect";
import api from "../../../services/api";
import toastError from "../../../errors/toastError";
import { i18n } from "../../../translate/i18n";
import { EMPTY, formatDateTime, formatDuration } from "./format";

const PAGE_SIZE = 25;
// Mesmo mínimo do servidor: com menos letras a busca não é aplicada.
const MIN_SEARCH_LENGTH = 2;
const STATUSES = ["running", "completed", "failed", "cancelled"];
const t = (key, options) => i18n.t(`flowResults.${key}`, options);

const STATUS_DOT = { running: "dotRunning", completed: "dotOk", failed: "dotError", cancelled: "dotOff" };

export const StateChip = ({ classes, status }) => (
  <span className={classes.stateChip}>
    <span className={classes[STATUS_DOT[status] || "dotOff"]} />
    {t(`status.${status}`)}
  </span>
);

export const reasonLabel = (endReason) => (endReason && i18n.exists(`flowResults.endReason.${endReason}`) ? t(`endReason.${endReason}`) : "");

// Envio por status de OS que parou antes de achar o contato (descadastro,
// telefone inválido): não houve contato, e não "contato removido".
export const contactName = (execution) =>
  execution.contact?.name ||
  execution.contact?.number ||
  t(execution.kind === "order_status" && execution.endReason !== "sent" ? "history.noContact" : "history.removedContact");

/** A OS que disparou a mensagem ("Atualização de OS"), com o status. */
export const orderLabel = (order) =>
  order ? (order.status ? t("history.orderStatus", { number: order.number, status: order.status }) : t("history.order", { number: order.number })) : "";

const MessagesCell = ({ classes, messages }) => {
  if (!messages.sent && !messages.failed) return <span className={classes.tdMuted}>{EMPTY}</span>;
  return (
    <>
      <div className={classes.nowrap}>
        {t("history.sentCount", { count: messages.sent })} · {t("history.deliveredCount", { count: messages.delivered })}
      </div>
      {messages.failed > 0 && <div className={classes.tdError}>{t("history.failedMessages", { count: messages.failed })}</div>}
    </>
  );
};

const StepCell = ({ classes, execution }) => {
  const label = execution.currentNode?.label || EMPTY;
  const reason = reasonLabel(execution.endReason);
  return (
    <>
      <div>{label}</div>
      <div className={classes.tdMuted}>{reason || t("history.steps", { count: execution.steps.length })}</div>
    </>
  );
};

const SkeletonRows = ({ classes }) => (
  <div aria-hidden="true">
    {[0, 1, 2, 3, 4].map((i) => (
      <div key={i} className={classes.skeleton} style={{ height: 44, marginBottom: 8 }} />
    ))}
  </div>
);

const ExecutionsHistory = ({ classes, workflowId, kinds, period, refreshKey, onOpen }) => {
  const isMobile = useMediaQuery((theme) => theme.breakpoints.down("xs"));
  const [rows, setRows] = useState([]);
  const [cursor, setCursor] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [failed, setFailed] = useState(false);
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [status, setStatus] = useState("");
  const [kind, setKind] = useState("");
  // Ignora respostas de buscas antigas (filtro trocado no meio do caminho).
  const requestSeq = useRef(0);

  useEffect(() => {
    const term = search.trim();
    const timer = setTimeout(() => setDebouncedSearch(term.length >= MIN_SEARCH_LENGTH ? term : ""), 400);
    return () => clearTimeout(timer);
  }, [search]);

  const fetchPage = useCallback(
    async (fromCursor) => {
      const seq = ++requestSeq.current;
      if (fromCursor) setLoadingMore(true);
      else setLoading(true);
      try {
        const { data } = await api.get(`/workflows/${workflowId}/executions`, {
          params: {
            limit: PAGE_SIZE,
            cursor: fromCursor || undefined,
            status: status || undefined,
            kind: kind || undefined,
            search: debouncedSearch || undefined,
            from: period.from,
            to: period.to,
          },
        });
        if (seq !== requestSeq.current) return;
        setRows((previous) => {
          if (!fromCursor) return data.executions;
          const known = new Set(previous.map((e) => e.id));
          return [...previous, ...data.executions.filter((e) => !known.has(e.id))];
        });
        setCursor(data.nextCursor);
        setFailed(false);
      } catch (err) {
        if (seq !== requestSeq.current) return;
        if (fromCursor) toastError(err);
        else setFailed(true);
      } finally {
        if (seq === requestSeq.current) {
          setLoading(false);
          setLoadingMore(false);
        }
      }
    },
    [workflowId, status, kind, debouncedSearch, period.from, period.to]
  );

  useEffect(() => {
    fetchPage(null);
  }, [fetchPage, refreshKey]);

  const filtering = !!(debouncedSearch || status || kind);

  return (
    <div className={classes.section}>
      <div className={classes.sectionHeader}>
        <div className={classes.sectionTitle}>{t("history.title")}</div>
        <div className={classes.sectionHint}>{t("history.subtitle")}</div>
      </div>

      <div className={classes.filters}>
        <div className={classes.searchWrap}>
          <Search size={14} className={classes.searchIcon} />
          <input
            type="search"
            className={classes.input}
            placeholder={t(kinds.includes("order_status") ? "history.searchWithOrder" : "history.search")}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            aria-label={t(kinds.includes("order_status") ? "history.searchWithOrder" : "history.search")}
          />
        </div>
        <ModernSelect
          size="small"
          className={classes.filterSelect}
          displayEmpty
          value={status}
          onChange={(e) => setStatus(e.target.value)}
          inputProps={{ "aria-label": t("history.columns.status") }}
        >
          <MenuItem value="">{t("history.allStatuses")}</MenuItem>
          {STATUSES.map((value) => (
            <MenuItem key={value} value={value}>
              {t(`status.${value}`)}
            </MenuItem>
          ))}
        </ModernSelect>
        {kinds.length > 1 && (
          <ModernSelect
            size="small"
            className={classes.filterSelect}
            displayEmpty
            value={kind}
            onChange={(e) => setKind(e.target.value)}
            inputProps={{ "aria-label": t("history.kind") }}
          >
            <MenuItem value="">{t("history.allKinds")}</MenuItem>
            {kinds.map((value) => (
              <MenuItem key={value} value={value}>
                {t(`kind.${value}`)}
              </MenuItem>
            ))}
          </ModernSelect>
        )}
      </div>

      {loading && <SkeletonRows classes={classes} />}

      {!loading && failed && (
        <div className={`${classes.banner} ${classes.bannerError}`} role="alert">
          <span style={{ flex: 1 }}>{t("errors.history")}</span>
          <button type="button" className={`${classes.secondaryBtn} ${classes.smallBtn}`} onClick={() => fetchPage(null)}>
            {t("errors.retry")}
          </button>
        </div>
      )}

      {!loading && !failed && rows.length === 0 && (
        <div className={classes.empty}>
          {filtering ? (
            t("history.empty.filtered")
          ) : (
            <>
              <History size={26} style={{ opacity: 0.4, marginBottom: 8 }} />
              <div className={classes.emptyTitle}>{t("history.empty.title")}</div>
              <div>{t("history.empty.description")}</div>
            </>
          )}
        </div>
      )}

      {!loading && !failed && rows.length > 0 && isMobile && (
        <div className={classes.cardList}>
          {rows.map((execution) => (
            <button key={execution.id} type="button" className={classes.mCard} onClick={() => onOpen(execution.id)}>
              <div className={classes.mCardTop}>
                <span className={classes.mCardName}>{contactName(execution)}</span>
                <StateChip classes={classes} status={execution.status} />
              </div>
              {execution.order && <div className={classes.tdMuted}>{orderLabel(execution.order)}</div>}
              <div className={classes.tdMuted}>
                {formatDateTime(execution.startedAt)} · {formatDuration(execution.durationMs)}
              </div>
              <div className={classes.tdMuted}>{execution.currentNode?.label || reasonLabel(execution.endReason) || EMPTY}</div>
              {execution.lastError?.message && execution.status === "failed" && (
                <div className={classes.tdError}>{execution.lastError.message}</div>
              )}
            </button>
          ))}
        </div>
      )}

      {!loading && !failed && rows.length > 0 && !isMobile && (
        <div className={classes.tableWrap}>
          <table className={classes.table}>
            <thead>
              <tr>
                <th className={classes.th}>{t("history.columns.contact")}</th>
                <th className={classes.th}>{t("history.columns.startedAt")}</th>
                <th className={classes.th}>{t("history.columns.status")}</th>
                <th className={classes.th}>{t("history.columns.step")}</th>
                <th className={classes.th}>{t("history.columns.duration")}</th>
                <th className={classes.th}>{t("history.columns.messages")}</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((execution) => (
                <tr
                  key={execution.id}
                  className={classes.tr}
                  tabIndex={0}
                  onClick={() => onOpen(execution.id)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") {
                      e.preventDefault();
                      onOpen(execution.id);
                    }
                  }}
                >
                  <td className={classes.td}>
                    <div>{contactName(execution)}</div>
                    {execution.contact?.name && <div className={classes.tdMuted}>{execution.contact.number}</div>}
                    {execution.order && <div className={classes.tdMuted}>{orderLabel(execution.order)}</div>}
                  </td>
                  <td className={`${classes.td} ${classes.nowrap}`}>{formatDateTime(execution.startedAt)}</td>
                  <td className={classes.td}>
                    <StateChip classes={classes} status={execution.status} />
                    {execution.lastError?.message && execution.status === "failed" && (
                      <div className={classes.tdError} title={execution.lastError.message}>
                        {execution.lastError.message}
                      </div>
                    )}
                  </td>
                  <td className={classes.td}>
                    <StepCell classes={classes} execution={execution} />
                  </td>
                  <td className={`${classes.td} ${classes.nowrap}`}>{formatDuration(execution.durationMs)}</td>
                  <td className={classes.td}>
                    <MessagesCell classes={classes} messages={execution.messages} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {!loading && !failed && cursor && (
        <div className={classes.loadMore}>
          <button type="button" className={classes.secondaryBtn} onClick={() => fetchPage(cursor)} disabled={loadingMore}>
            {loadingMore ? t("history.loading") : t("history.loadMore")}
          </button>
        </div>
      )}
    </div>
  );
};

export default ExecutionsHistory;
